import { useEffect, useRef, useState } from "react";
import type { SubtitleVersion } from "@/mocks/subtitles";

interface DownloadSectionProps {
  versions: SubtitleVersion[];
  title: string;
}

interface StatusState {
  state: "idle" | "loading" | "done";
  message: string;
}

export default function DownloadSection({ versions, title }: DownloadSectionProps) {
  const [status, setStatus] = useState<StatusState>({ state: "idle", message: "" });
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const handleDownload = (version: SubtitleVersion) => {
    const fileName = `${title}.${version.format}`;
    setStatus({ state: "loading", message: `正在准备「${version.label}」字幕文件…` });
    timerRef.current = window.setTimeout(() => {
      const content = version.lines.join("\n");
      const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setStatus({ state: "done", message: `演示下载已开始：${fileName}` });
    }, 800);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
        <i className="ri-download-2-line text-lg text-accent-600"></i>
        字幕下载
      </div>

      <div className="space-y-3">
        {versions.map((version) => (
          <div
            key={version.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-background-200 bg-background-100 p-3"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground-900">{version.label}</p>
              <p className="mt-0.5 text-xs text-foreground-500">
                {version.language} · {version.format.toUpperCase()} · {version.size}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleDownload(version)}
              disabled={status.state === "loading"}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-600 disabled:cursor-wait disabled:opacity-70"
            >
              <i
                className={
                  status.state === "loading"
                    ? "ri-loader-4-line animate-spin"
                    : "ri-download-line"
                }
              ></i>
              下载
            </button>
          </div>
        ))}
      </div>

      {status.message && (
        <div
          className={`mt-4 flex items-start gap-2 rounded-md px-3 py-2.5 text-xs ${
            status.state === "done"
              ? "bg-secondary-100 text-secondary-900"
              : "bg-primary-100 text-primary-700"
          }`}
        >
          <i
            className={
              status.state === "done" ? "ri-checkbox-circle-line text-sm" : "ri-loader-4-line animate-spin text-sm"
            }
          ></i>
          <span>{status.message}</span>
        </div>
      )}

      <p className="mt-4 flex items-start gap-1.5 text-xs leading-relaxed text-foreground-400">
        <i className="ri-information-line mt-0.5 text-sm"></i>
        当前为前端演示下载，实际字幕文件下载需后端存储支持；请下载后自行核对时间轴与片源版本。
      </p>
    </div>
  );
}